/**
 * Passio-branded email broadcast composer.
 *
 * Used in two places:
 *   1. The Customer Hub bulk-action bar  (multi-recipient, by tier
 *      or selected customers).
 *   2. The Customer Hub per-row "Send email" action (1 recipient
 *      via a `customer_ids: [single]` recipient spec).
 *
 * UX layout:
 *   left pane  — composer (subject, title, intro, body, CTA, alert)
 *   right pane — live preview rendered server-side via
 *                POST /admin/customer-hub/email-preview so the
 *                operator sees byte-for-byte what the customer will
 *                see.
 *
 * Why server-side preview rather than rendering in JS:
 *   the markdown -> branded HTML pipeline is in Python; duplicating
 *   it in JS is the standard way to get drift between preview and
 *   reality. Roundtripping each keystroke is wasteful, so we debounce
 *   for 400ms.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import VisibilityIcon from '@mui/icons-material/Visibility';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useAuth } from '../contexts/AuthContext';
import { getConfig } from '../services/config';
import MarkdownToolbar from './MarkdownToolbar';

// ---------- Types matching the backend schemas ----------------------

export type RecipientMode =
  | 'all'
  | 'all_paid'
  | 'by_tier'
  | 'by_status'
  | 'zdr_only';

export interface EmailRecipientsSpec {
  customer_ids?: string[];
  mode?: RecipientMode;
  tiers?: string[];
  statuses?: string[];
  include_inactive?: boolean;
}

export interface EmailBodySpec {
  subject: string;
  title: string;
  intro?: string;
  body_markdown?: string;
  body_html?: string;
  cta_label?: string;
  cta_url?: string;
  alert_severity?: 'info' | 'warning' | 'danger';
  alert_title?: string;
  alert_body?: string;
  footer_note?: string;
}

interface SkippedRecipient {
  customer_id: string;
  reason: 'not_found' | 'inactive' | 'platform_admin';
}

interface BroadcastResponse {
  dry_run: boolean;
  test_send: boolean;
  recipients_requested?: number | null;
  recipients_resolved: number;
  sent: number;
  failed: number;
  no_email?: number;
  skipped_ineligible?: number;
  sample_recipient_ids: string[];
  failed_recipient_ids: string[];
  no_email_recipient_ids?: string[];
  skipped_detail?: SkippedRecipient[];
}

const SKIP_REASON_LABELS: Record<string, string> = {
  not_found: 'not found',
  inactive: 'inactive',
  platform_admin: 'platform-admin (never emailed)',
};

/** Collapse the per-ID skip list into a short "2 inactive, 1 not found"
 *  style summary for the result banner. */
function summarizeSkips(detail?: SkippedRecipient[]): string {
  if (!detail || detail.length === 0) return '';
  const counts: Record<string, number> = {};
  detail.forEach((s) => {
    counts[s.reason] = (counts[s.reason] || 0) + 1;
  });
  return Object.entries(counts)
    .map(([reason, n]) => `${n} ${SKIP_REASON_LABELS[reason] || reason}`)
    .join(', ');
}

/**
 * Render a backend error response into a single human-readable line.
 *
 * Handles the three shapes we actually see in this app:
 *   1. FastAPI/Pydantic v2 validation: `{ detail: [{ loc, msg, ... }] }`
 *      (an ARRAY — naively stringifying it gives "[object Object]").
 *   2. FastAPI HTTPException: `{ detail: "some message" }` (a string).
 *   3. Our `create_response`-style failures: `{ message: "..." }`.
 *
 * Falls back to `HTTP <status>` so the user always sees something.
 */
function formatBackendError(json: any, status: number): string {
  const detail = json?.detail;
  if (Array.isArray(detail)) {
    // Pydantic validation errors. Surface field path + message so
    // the operator knows WHICH input is wrong, not just "invalid".
    const lines = detail
      .map((d: any) => {
        const loc = Array.isArray(d?.loc)
          ? d.loc.filter((p: any) => p !== 'body').join('.')
          : '';
        const msg = d?.msg || 'invalid';
        return loc ? `${loc}: ${msg}` : msg;
      })
      .filter(Boolean);
    if (lines.length) return lines.join('; ');
  }
  if (typeof detail === 'string' && detail) return detail;
  if (typeof json?.message === 'string' && json.message) return json.message;
  return `HTTP ${status}`;
}

// ---------- Component props -----------------------------------------

interface EmailBroadcastDialogProps {
  open: boolean;
  onClose: () => void;
  /** Preselected recipient spec from the Customer Hub. The dialog
   *  shows it as a chip and lets the operator edit before sending. */
  initialRecipients: EmailRecipientsSpec;
  /** Optional human-readable description of the recipient set
   *  (e.g. "47 selected customers", "All Pro tier"). */
  initialRecipientsLabel?: string;
  /** Available tier options for the by_tier picker. */
  availableTiers?: string[];
  onSendComplete?: (result: BroadcastResponse) => void;
}

const DEFAULT_TIERS = ['starter', 'builder', 'pro', 'scale'];
const DEFAULT_STATUSES = ['active', 'trialing', 'past_due', 'unpaid', 'canceled'];

// ---------- The component -------------------------------------------

const EmailBroadcastDialog: React.FC<EmailBroadcastDialogProps> = ({
  open,
  onClose,
  initialRecipients,
  initialRecipientsLabel,
  availableTiers = DEFAULT_TIERS,
  onSendComplete,
}) => {
  const { getIdToken, user } = useAuth();
  const apiBase = useMemo(() => getConfig().apiBaseUrl, []);

  // Composer state
  const [subject, setSubject] = useState('');
  const [title, setTitle] = useState('');
  const [intro, setIntro] = useState('Hi there,');
  const [bodyMarkdown, setBodyMarkdown] = useState('');
  const [rawHtmlMode, setRawHtmlMode] = useState(false);
  const [bodyHtml, setBodyHtml] = useState('');

  const [ctaLabel, setCtaLabel] = useState('');
  const [ctaUrl, setCtaUrl] = useState('');

  const [alertEnabled, setAlertEnabled] = useState(false);
  const [alertSeverity, setAlertSeverity] = useState<'info' | 'warning' | 'danger'>('info');
  const [alertTitle, setAlertTitle] = useState('');
  const [alertBody, setAlertBody] = useState('');

  const [footerNote, setFooterNote] = useState(
    'You\'re receiving this email because you have a Passio AI account.'
  );

  const [reason, setReason] = useState('');

  // Recipients state (editable from the seed value)
  const [recipients, setRecipients] = useState<EmailRecipientsSpec>(initialRecipients);
  const [recipientsLabel, setRecipientsLabel] = useState(initialRecipientsLabel || '');
  const [testRecipientEmail, setTestRecipientEmail] = useState('');

  // Preview state
  const [previewHtml, setPreviewHtml] = useState<string>('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  // Send state
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sendResult, setSendResult] = useState<BroadcastResponse | null>(null);

  const previewDebounce = useRef<number | undefined>(undefined);
  // Ref to the body <textarea> so MarkdownToolbar can apply
  // selection-aware transforms. Wired through TextField's
  // `inputRef` prop below.
  const bodyTextareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Reseed from props each time the dialog opens so the previous
  // composer state doesn't leak between sessions.
  useEffect(() => {
    if (open) {
      setRecipients(initialRecipients);
      setRecipientsLabel(initialRecipientsLabel || '');
      setSendResult(null);
      setSendError(null);
    }
  }, [open, initialRecipients, initialRecipientsLabel]);

  // Build the EmailBodySpec the backend expects.
  const buildEmailSpec = (): EmailBodySpec => {
    const spec: EmailBodySpec = {
      subject: subject.trim(),
      title: title.trim() || subject.trim(),
      intro: intro.trim() || undefined,
      footer_note: footerNote.trim() || undefined,
    };
    if (rawHtmlMode) {
      spec.body_html = bodyHtml;
    } else {
      spec.body_markdown = bodyMarkdown;
    }
    if (ctaLabel.trim() && ctaUrl.trim()) {
      spec.cta_label = ctaLabel.trim();
      spec.cta_url = ctaUrl.trim();
    }
    if (alertEnabled && (alertTitle.trim() || alertBody.trim())) {
      spec.alert_severity = alertSeverity;
      spec.alert_title = alertTitle.trim() || undefined;
      spec.alert_body = alertBody.trim() || undefined;
    }
    return spec;
  };

  // ---- Server-side preview (debounced) -----------------------------
  const refreshPreview = async () => {
    setPreviewError(null);
    if (!subject.trim() || !title.trim() || (!bodyMarkdown.trim() && !bodyHtml.trim())) {
      setPreviewHtml('');
      return;
    }
    setPreviewLoading(true);
    try {
      const token = await getIdToken();
      const resp = await fetch(`${apiBase}/api/v2/admin/customer-hub/email-preview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ email: buildEmailSpec() }),
      });
      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(`HTTP ${resp.status}: ${txt}`);
      }
      const json = await resp.json();
      setPreviewHtml(json?.data?.html || '');
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setPreviewError(`Preview failed: ${msg}`);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Re-render the preview whenever any composer field changes.
  // Debounce so we don't pound the API on every keystroke.
  useEffect(() => {
    if (!open) return;
    window.clearTimeout(previewDebounce.current);
    previewDebounce.current = window.setTimeout(refreshPreview, 400);
    return () => window.clearTimeout(previewDebounce.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    subject,
    title,
    intro,
    bodyMarkdown,
    bodyHtml,
    rawHtmlMode,
    ctaLabel,
    ctaUrl,
    alertEnabled,
    alertSeverity,
    alertTitle,
    alertBody,
    footerNote,
  ]);

  // ---- Send paths --------------------------------------------------

  const send = async (opts: { dry_run?: boolean; test?: boolean }) => {
    setSendError(null);
    setSendResult(null);
    // Match the backend Pydantic constraints exactly so the operator
    // gets immediate feedback instead of a 422 round-trip:
    //   subject/title    → min_length=1
    //   body_markdown    → free-form (we still require non-empty)
    //   reason           → min_length=3
    if (!subject.trim() || !title.trim() || (!bodyMarkdown.trim() && !bodyHtml.trim())) {
      setSendError('Subject, title, and body are required.');
      return;
    }
    if (reason.trim().length < 3) {
      setSendError('Reason for audit log must be at least 3 characters.');
      return;
    }
    if (opts.test && !testRecipientEmail.trim()) {
      setSendError('Test recipient email is required.');
      return;
    }
    setSending(true);
    try {
      const token = await getIdToken();
      const body: any = {
        recipients,
        email: buildEmailSpec(),
        reason: reason.trim(),
        dry_run: !!opts.dry_run,
      };
      if (opts.test) body.test_recipient_email = testRecipientEmail.trim();

      const resp = await fetch(`${apiBase}/api/v2/admin/customer-hub/email-broadcast`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      const json = await resp.json();
      if (!resp.ok) {
        throw new Error(formatBackendError(json, resp.status));
      }
      const data = json?.data as BroadcastResponse;
      setSendResult(data);
      if (onSendComplete && !opts.dry_run && !opts.test) onSendComplete(data);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setSendError(`Send failed: ${msg}`);
    } finally {
      setSending(false);
    }
  };

  // Self-test convenience: prefill the test recipient with the
  // logged-in admin's own email.
  const handlePrefillSelfTest = () => {
    const myEmail = user?.email;
    if (myEmail) setTestRecipientEmail(myEmail);
  };

  // ---- Render ------------------------------------------------------

  return (
    <Dialog open={open} onClose={sending ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <SendIcon color="primary" />
          <Typography variant="h6" fontWeight={600}>
            Compose Broadcast Email
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <IconButton onClick={onClose} disabled={sending}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 0 }}>
          {/* ----- LEFT: Composer ----- */}
          <Box sx={{ p: 3, borderRight: { md: '1px solid', borderColor: 'divider' } }}>
            <Stack spacing={2}>
              <RecipientsPicker
                recipients={recipients}
                setRecipients={setRecipients}
                label={recipientsLabel}
                setLabel={setRecipientsLabel}
                availableTiers={availableTiers}
              />

              <Divider textAlign="left">Email content</Divider>

              <TextField
                label="Subject line"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                fullWidth
                size="small"
                helperText="What appears in the recipient's inbox preview."
              />
              <TextField
                label="Header title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                fullWidth
                size="small"
                helperText="Big white heading on the gradient header in the email."
              />
              <TextField
                label="Greeting"
                value={intro}
                onChange={(e) => setIntro(e.target.value)}
                fullWidth
                size="small"
                helperText="Shown at the top of the body — e.g. 'Hi there,'"
              />

              <Stack direction="row" alignItems="center" spacing={1}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={rawHtmlMode}
                      onChange={(e) => setRawHtmlMode(e.target.checked)}
                    />
                  }
                  label="Raw HTML body"
                />
                <Tooltip title="By default we apply Passio styling automatically: links/lists/bold come out on-brand. Switch this on to paste your own HTML instead. The outer template (header, footer, container) is still applied either way.">
                  <HelpOutlineIcon fontSize="small" color="action" />
                </Tooltip>
              </Stack>

              {rawHtmlMode ? (
                <TextField
                  label="HTML body"
                  value={bodyHtml}
                  onChange={(e) => setBodyHtml(e.target.value)}
                  fullWidth
                  multiline
                  minRows={8}
                  size="small"
                  helperText="Raw HTML. Wrapped in the standard Passio chrome on send."
                />
              ) : (
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ display: 'block', mb: 0.5, color: 'text.secondary', fontWeight: 500 }}
                  >
                    Body (Markdown)
                  </Typography>
                  <MarkdownToolbar
                    textareaRef={bodyTextareaRef as React.RefObject<HTMLTextAreaElement>}
                    value={bodyMarkdown}
                    onChange={setBodyMarkdown}
                  />
                  <TextField
                    value={bodyMarkdown}
                    onChange={(e) => setBodyMarkdown(e.target.value)}
                    fullWidth
                    multiline
                    minRows={10}
                    size="small"
                    inputRef={bodyTextareaRef}
                    placeholder={
                      'Type your message here…\n\nUse the toolbar above to format. ' +
                      'Supports **bold**, *italic*, links, bullet lists, ordered lists, ' +
                      '`code`, --- horizontal rules, and ![alt](https://...) images.'
                    }
                    helperText="Live preview on the right shows the final branded HTML."
                  />
                </Box>
              )}

              <Stack direction="row" spacing={2}>
                <TextField
                  label="CTA button label (optional)"
                  value={ctaLabel}
                  onChange={(e) => setCtaLabel(e.target.value)}
                  size="small"
                  sx={{ flex: 1 }}
                />
                <TextField
                  label="CTA URL"
                  value={ctaUrl}
                  onChange={(e) => setCtaUrl(e.target.value)}
                  size="small"
                  sx={{ flex: 2 }}
                  placeholder="https://..."
                />
              </Stack>

              <Divider textAlign="left">Optional alert banner</Divider>
              <FormControlLabel
                control={
                  <Switch
                    checked={alertEnabled}
                    onChange={(e) => setAlertEnabled(e.target.checked)}
                  />
                }
                label="Show coloured alert at the top of the email"
              />
              {alertEnabled && (
                <Stack spacing={1.5}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Severity</InputLabel>
                    <Select
                      label="Severity"
                      value={alertSeverity}
                      onChange={(e) => setAlertSeverity(e.target.value as any)}
                    >
                      <MenuItem value="info">Info (blue)</MenuItem>
                      <MenuItem value="warning">Warning (yellow)</MenuItem>
                      <MenuItem value="danger">Danger (red)</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    label="Alert title"
                    size="small"
                    value={alertTitle}
                    onChange={(e) => setAlertTitle(e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="Alert body"
                    size="small"
                    value={alertBody}
                    onChange={(e) => setAlertBody(e.target.value)}
                    fullWidth
                    multiline
                    minRows={2}
                  />
                </Stack>
              )}

              <Divider textAlign="left">Footer note (optional)</Divider>
              <TextField
                label="Footer note"
                value={footerNote}
                onChange={(e) => setFooterNote(e.target.value)}
                size="small"
                fullWidth
                multiline
                minRows={2}
                helperText="Small-text line in the footer explaining why the recipient got this email. Strongly recommended for inbox-provider deliverability."
              />

              <Divider textAlign="left">Audit log</Divider>
              <TextField
                label="Reason (required for audit log)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                size="small"
                fullWidth
                placeholder="e.g. Service incident notice 2026-05-24"
                helperText="Lands in [AI_AUDIT] customer_hub log lines."
              />
            </Stack>
          </Box>

          {/* ----- RIGHT: Preview ----- */}
          <Box sx={{ p: 3, bgcolor: 'grey.50' }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
              <VisibilityIcon fontSize="small" color="action" />
              <Typography variant="subtitle2" fontWeight={600}>
                Live preview
              </Typography>
              {previewLoading && (
                <Typography variant="caption" color="textSecondary">
                  rendering…
                </Typography>
              )}
            </Stack>
            {previewError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {previewError}
              </Alert>
            )}
            {previewHtml ? (
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  overflow: 'hidden',
                  bgcolor: 'background.paper',
                  height: 600,
                }}
              >
                {/* Iframe so the email's CSS can't mess with the
                    portal's CSS, and vice versa. */}
                <iframe
                  title="Email preview"
                  srcDoc={previewHtml}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                  }}
                />
              </Box>
            ) : (
              <Alert severity="info">
                Fill in the subject, header title, and body to see a live
                preview here.
              </Alert>
            )}
          </Box>
        </Box>
      </DialogContent>

      {/* ----- Footer (results / actions) ----- */}
      <Box sx={{ px: 3, pt: 2, pb: 0 }}>
        {sendError && <Alert severity="error" sx={{ mb: 2 }}>{sendError}</Alert>}
        {sendResult && (() => {
          const r = sendResult;
          const noEmail = r.no_email ?? 0;
          const skipped = r.skipped_ineligible ?? 0;
          const skipSummary = summarizeSkips(r.skipped_detail);

          // --- Test send ---
          if (r.test_send) {
            return (
              <Alert severity={r.sent ? 'success' : 'error'} sx={{ mb: 2 }}>
                {r.sent
                  ? 'Test email accepted by the mail provider.'
                  : 'Test email was rejected by the mail provider.'}
              </Alert>
            );
          }

          // --- Dry run ---
          if (r.dry_run) {
            return (
              <Alert
                severity={r.recipients_resolved === 0 ? 'warning' : 'info'}
                sx={{ mb: 2 }}
              >
                {r.recipients_resolved === 0
                  ? 'No eligible recipients — this broadcast would send nothing.'
                  : `Dry run: ${r.recipients_resolved} eligible recipient(s) would be emailed (nothing sent).`}
                {skipped > 0 && (
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    Skipped {skipped}
                    {r.recipients_requested != null
                      ? ` of ${r.recipients_requested} selected`
                      : ''}
                    {skipSummary ? `: ${skipSummary}` : ''}.
                  </Typography>
                )}
              </Alert>
            );
          }

          // --- Live send ---
          const nothingDelivered = r.sent === 0;
          const hasProblems =
            r.failed > 0 || noEmail > 0 || skipped > 0 || r.recipients_resolved === 0;
          const severity = nothingDelivered
            ? 'error'
            : hasProblems
            ? 'warning'
            : 'success';

          return (
            <Alert severity={severity} sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight={600}>
                {r.recipients_resolved === 0
                  ? 'No eligible recipients — nothing was sent.'
                  : `Sent ${r.sent} of ${r.recipients_resolved} eligible recipient(s).`}
              </Typography>
              {(r.failed > 0 || noEmail > 0 || skipped > 0) && (
                <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2 }}>
                  {r.failed > 0 && (
                    <li>
                      <Typography variant="body2">
                        {r.failed} rejected by the mail provider.
                      </Typography>
                    </li>
                  )}
                  {noEmail > 0 && (
                    <li>
                      <Typography variant="body2">
                        {noEmail} had no email address on file.
                      </Typography>
                    </li>
                  )}
                  {skipped > 0 && (
                    <li>
                      <Typography variant="body2">
                        {skipped}
                        {r.recipients_requested != null
                          ? ` of ${r.recipients_requested} selected`
                          : ''}{' '}
                        skipped{skipSummary ? `: ${skipSummary}` : ''}.
                      </Typography>
                    </li>
                  )}
                </Box>
              )}
            </Alert>
          );
        })()}
      </Box>

      <DialogActions sx={{ p: 3, gap: 1, flexWrap: 'wrap' }}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ flex: 1, minWidth: 0, flexWrap: 'wrap' }}
        >
          <TextField
            label="Test send to"
            size="small"
            value={testRecipientEmail}
            onChange={(e) => setTestRecipientEmail(e.target.value)}
            placeholder="email@example.com"
          />
          <Button onClick={handlePrefillSelfTest} disabled={!user?.email}>
            Use my email
          </Button>
          <Button
            onClick={() => send({ test: true })}
            disabled={sending || !testRecipientEmail}
            variant="outlined"
          >
            Send test
          </Button>
        </Stack>

        <Button onClick={onClose} disabled={sending}>
          Cancel
        </Button>
        <Button
          onClick={() => send({ dry_run: true })}
          disabled={sending}
          variant="outlined"
          color="warning"
        >
          Dry run
        </Button>
        <Button
          onClick={() => send({})}
          disabled={sending}
          variant="contained"
          color="primary"
          startIcon={<SendIcon />}
        >
          {sending ? 'Sending…' : 'Send broadcast'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------- Recipient sub-picker (separate to keep the main component
//             readable). -----------------------------------------------

interface RecipientsPickerProps {
  recipients: EmailRecipientsSpec;
  setRecipients: (r: EmailRecipientsSpec) => void;
  label: string;
  setLabel: (l: string) => void;
  availableTiers: string[];
}

const RecipientsPicker: React.FC<RecipientsPickerProps> = ({
  recipients,
  setRecipients,
  label,
  setLabel,
  availableTiers,
}) => {
  // If a list of customer_ids is provided, the dialog renders that as
  // a simple chip and doesn't expose the mode picker — the operator
  // already chose recipients in the table. This keeps the picker
  // simple in the most common path.
  const isExplicitList = Array.isArray(recipients.customer_ids) && recipients.customer_ids.length > 0;

  if (isExplicitList) {
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="subtitle2" fontWeight={600}>
          Recipients:
        </Typography>
        <Chip
          color="primary"
          label={
            label || `${recipients.customer_ids?.length ?? 0} selected customer(s)`
          }
        />
      </Stack>
    );
  }

  const mode: RecipientMode = recipients.mode || 'all_paid';

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="subtitle2" fontWeight={600}>
          Recipients:
        </Typography>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Mode</InputLabel>
          <Select
            label="Mode"
            value={mode}
            onChange={(e) =>
              setRecipients({ ...recipients, mode: e.target.value as RecipientMode })
            }
          >
            <MenuItem value="all_paid">All paid (active/trialing)</MenuItem>
            <MenuItem value="all">All customers</MenuItem>
            <MenuItem value="by_tier">By tier…</MenuItem>
            <MenuItem value="by_status">By status…</MenuItem>
            <MenuItem value="zdr_only">ZDR-enabled only</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {mode === 'by_tier' && (
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {availableTiers.map((t) => {
            const checked = (recipients.tiers || []).includes(t);
            return (
              <Chip
                key={t}
                label={t}
                color={checked ? 'primary' : 'default'}
                onClick={() => {
                  const cur = new Set(recipients.tiers || []);
                  if (cur.has(t)) cur.delete(t);
                  else cur.add(t);
                  setRecipients({ ...recipients, tiers: Array.from(cur) });
                }}
                variant={checked ? 'filled' : 'outlined'}
              />
            );
          })}
        </Stack>
      )}

      {mode === 'by_status' && (
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {DEFAULT_STATUSES.map((s) => {
            const checked = (recipients.statuses || []).includes(s);
            return (
              <Chip
                key={s}
                label={s}
                color={checked ? 'primary' : 'default'}
                onClick={() => {
                  const cur = new Set(recipients.statuses || []);
                  if (cur.has(s)) cur.delete(s);
                  else cur.add(s);
                  setRecipients({ ...recipients, statuses: Array.from(cur) });
                }}
                variant={checked ? 'filled' : 'outlined'}
              />
            );
          })}
        </Stack>
      )}

      <FormControlLabel
        control={
          <Checkbox
            size="small"
            checked={!!recipients.include_inactive}
            onChange={(e) =>
              setRecipients({ ...recipients, include_inactive: e.target.checked })
            }
          />
        }
        label="Also include inactive customers"
      />
    </Stack>
  );
};

export default EmailBroadcastDialog;
